// 這週重點 — the top of the 每日看板: ONE recommended priority for the week, with two
// alternatives and the trade-offs, so the board answers "what matters most" instead of
// leaving that to Magnus (2026-10-16). Written by the Monday 每日檢查 run through the
// workspace scripts/oms/weekly_focus.py; `metrics` is recomputed every 30 minutes.
// He picks 選這個 / 改選 A / 改選 B → POST /api/owner-board/focus/:id/choose.

import React from 'react';
import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AgentThread, Decision } from '../agent/agentShared';

export type FocusTone = 'good' | 'warn' | 'bad' | 'neutral';

export interface FocusStep {
  state: 'done' | 'you' | 'claude' | 'when';
  label: string;
  sub?: string;
  thread_id?: number;
  card_id?: number;
  date?: string;
}

export interface FocusAlternative {
  key: 'A' | 'B';
  direction: string;
  title: string;
  metric?: string;
  what: string;
  why_not: string;
  cost?: string;
  card_id?: number;
}

export interface FocusRow {
  id: number;
  week_of: string;
  direction: string;
  title: string;
  status: 'proposed' | 'chosen' | 'closed';
  chosen: 'main' | 'A' | 'B' | null;
  chosen_at: string | null;
  updated_at: string;
  spec: {
    title: string;
    headline: string;
    recommendation: string;
    rationale?: string;
    steps: FocusStep[];
    why: string;
    cost: string;
    postponed: string;
    unknowns: string;
    success: string;
    checkpoint_date?: string;
    alternatives: FocusAlternative[];
    tiles?: { v: string; l: string; tone?: FocusTone }[];
  };
  metrics: {
    tiles?: { v: string; l: string; tone?: FocusTone }[];
    series?: {
      label: string;
      line: number | null;
      as_of: string;
      points: { x: string; v: number; proj?: number | null; partial?: boolean }[];
    };
  } | null;
  review: { worked: string; learned: string; next: string; at: string } | null;
}

const TONE: Record<FocusTone, string> = { good: '#1d7a45', warn: '#9a6200', bad: '#b3261e', neutral: 'inherit' };
const ACCENT = '#1f5b4c';

const fmtDay = (iso?: string) => (iso ? `${Number(iso.slice(5, 7))}-${iso.slice(8, 10)}` : '');

// Weekly bars, drawn to one scale: full weeks grey (red under the line), this week solid
// with its projection dashed above it.
const SeriesChart: React.FC<{ series: NonNullable<FocusRow['metrics']>['series'] }> = ({ series }) => {
  if (!series || !series.points.length) return null;
  const pts = series.points;
  const max = Math.max(...pts.map((p) => Math.max(p.v, p.proj || 0)), series.line || 0) * 1.12 || 1;
  const W = 640;
  const H = 200;
  const base = 172;
  const top = 18;
  const x0 = 36;
  const slot = (W - x0 - 6) / pts.length;
  const bw = slot * 0.62;
  const y = (v: number) => base - (v / max) * (base - top);
  return (
    <Box component="svg" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={series.label} sx={{ width: '100%', height: 'auto', display: 'block' }}>
      {pts.map((p, i) => {
        const x = x0 + i * slot + (slot - bw) / 2;
        const below = series.line != null && !p.partial && p.v < series.line;
        return (
          <g key={p.x}>
            {p.partial && p.proj != null && p.proj > p.v && (
              <rect x={x} y={y(p.proj)} width={bw} height={y(p.v) - y(p.proj)} rx={2} fill="none" stroke={ACCENT} strokeDasharray="3 3" />
            )}
            <rect
              x={x}
              y={y(p.v)}
              width={bw}
              height={base - y(p.v)}
              rx={2}
              fill={p.partial ? ACCENT : below ? '#c0453b' : '#b9c4bf'}
              opacity={below ? 0.8 : 1}
            />
            <text x={x + bw / 2} y={y(p.partial && p.proj ? Math.max(p.proj, p.v) : p.v) - 4} textAnchor="middle" fontSize={11} fill="currentColor" opacity={0.75} fontWeight={p.partial ? 700 : 400}>
              {p.partial && p.proj ? `≈${p.proj}` : p.v}
            </text>
            <text x={x + bw / 2} y={base + 16} textAnchor="middle" fontSize={11} fill="currentColor" opacity={0.65} fontWeight={p.partial ? 700 : 400}>
              {p.x}
            </text>
          </g>
        );
      })}
      {series.line != null && (
        <>
          <line x1={x0 - 4} x2={W - 4} y1={y(series.line)} y2={y(series.line)} stroke="#c0453b" strokeDasharray="4 4" strokeWidth={1.2} />
          <text x={x0 - 8} y={y(series.line) + 4} textAnchor="end" fontSize={11} fill="#c0453b">
            {series.line}
          </text>
        </>
      )}
    </Box>
  );
};

interface Props {
  focus: FocusRow;
  decisions: Decision[];
  threads: AgentThread[];
  busy?: boolean;
  onChoose: (option: 'main' | 'A' | 'B') => void;
  onOpenStep: (step: FocusStep) => void;
}

const FocusCard: React.FC<Props> = ({ focus, decisions, threads, busy, onChoose, onOpenStep }) => {
  const s = focus.spec;
  const steps = Array.isArray(s.steps) ? s.steps : [];
  const alternatives = Array.isArray(s.alternatives) ? s.alternatives : [];
  const tiles = focus.metrics?.tiles || s.tiles || [];
  const chosenAlt = focus.chosen && focus.chosen !== 'main' ? alternatives.find((a) => a.key === focus.chosen) : null;
  // Last week's card stays up (with its Sunday review) until Monday's proposal; it can't be chosen any more.
  const monday = (() => {
    const tpe = new Date(Date.now() + 8 * 3600 * 1000);
    tpe.setUTCDate(tpe.getUTCDate() - ((tpe.getUTCDay() + 6) % 7));
    return tpe.toISOString().slice(0, 10);
  })();
  const stale = focus.week_of < monday;

  // A step tied to a conversation shows that conversation's live state.
  const stepState = (st: FocusStep): { mark: string; chip?: { label: string; color: 'error' | 'primary' | 'success' | 'default' } } => {
    if (st.thread_id) {
      if (decisions.some((d) => d.thread_id === st.thread_id)) return { mark: '!', chip: { label: '等你核准', color: 'error' } };
      const t = threads.find((x) => x.id === st.thread_id);
      if (t && (t.status === 'running' || t.status === 'queued')) return { mark: '…', chip: { label: 'Claude 在做', color: 'primary' } };
      if (t && t.status === 'idle') return { mark: '✓', chip: { label: '完成', color: 'success' } };
    }
    if (st.state === 'done') return { mark: '✓' };
    if (st.state === 'when') return { mark: '⏰' };
    return { mark: '' };
  };

  let n = 0;
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3, display: 'grid', gap: 1.75, minWidth: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
        <Typography variant="caption" sx={{ letterSpacing: 1, color: 'text.secondary' }}>
          {stale ? '上週重點' : '這週重點'} · {fmtDay(focus.week_of)} 起
        </Typography>
        {focus.status === 'chosen' && (
          <Chip size="small" label={focus.chosen === 'main' ? '你選了這個' : `你改選 ${focus.chosen}`} sx={{ bgcolor: '#e3eee9', color: ACCENT, fontWeight: 700 }} />
        )}
        {focus.metrics?.series?.as_of && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            數字 {focus.metrics.series.as_of}
          </Typography>
        )}
      </Stack>

      {chosenAlt && (
        <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: '#e3eee9' }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            這週改做：{chosenAlt.title} · {chosenAlt.what}
          </Typography>
        </Box>
      )}

      <Typography component="h2" sx={{ fontFamily: '"Noto Serif TC", Georgia, serif', fontWeight: 700, fontSize: { xs: 21, md: 24 }, lineHeight: 1.35, textWrap: 'balance' } as any}>
        {s.title}：{s.headline}
      </Typography>

      {tiles.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(tiles.length, 3)}, minmax(0, 1fr))`, gap: 1 }}>
          {tiles.map((t, i) => (
            <Box key={`${i}-${t.l}`} sx={{ minWidth: 0 }}>
              <Typography sx={{ fontFamily: '"IBM Plex Mono", ui-monospace, monospace', fontSize: { xs: 19, md: 22 }, fontWeight: 500, color: TONE[t.tone || 'neutral'], fontVariantNumeric: 'tabular-nums' }}>
                {t.v}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>
                {t.l}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {focus.metrics?.series && <SeriesChart series={focus.metrics.series} />}

      <Box sx={{ borderLeft: `3px solid ${ACCENT}`, pl: 1.5, display: 'grid', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          建議
        </Typography>
        <Typography sx={{ fontWeight: 700, fontSize: 16.5 }}>{s.recommendation}</Typography>
        {s.rationale && <Typography variant="body2">{s.rationale}</Typography>}
      </Box>

      <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1 }}>
        {steps.map((st, i) => {
          const live = stepState(st);
          const numbered = !live.mark && st.state !== 'done' && st.state !== 'when';
          if (numbered) n += 1;
          const clickable = !!st.thread_id || !!st.card_id;
          return (
            <Box
              component="li"
              key={i}
              onClick={clickable ? () => onOpenStep(st) : undefined}
              sx={{ display: 'grid', gridTemplateColumns: '24px minmax(0,1fr) auto', gap: 1, alignItems: 'start', cursor: clickable ? 'pointer' : 'default' }}
            >
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  border: 1.5,
                  borderStyle: st.state === 'when' ? 'dashed' : 'solid',
                  borderColor: live.mark === '✓' ? '#1d7a45' : live.mark === '!' ? '#b3261e' : st.state === 'you' ? ACCENT : 'divider',
                  color: live.mark === '✓' ? '#1d7a45' : live.mark === '!' ? '#b3261e' : ACCENT,
                  bgcolor: live.mark === '✓' ? '#e5f3ea' : 'transparent',
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 12,
                  fontWeight: 700,
                  mt: '1px',
                }}
              >
                {live.mark || (numbered ? n : '')}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: st.state === 'you' ? 700 : 500 }}>
                  {st.label}
                </Typography>
                {st.sub && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
                    {st.sub}
                  </Typography>
                )}
              </Box>
              {live.chip ? <Chip size="small" color={live.chip.color} label={live.chip.label} /> : <span />}
            </Box>
          );
        })}
      </Box>

      <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: '64px minmax(0,1fr)', gap: '6px 10px', fontSize: 14 }}>
        {(
          [
            ['為什麼', s.why],
            ['代價', s.cost],
            ['先放著', s.postponed],
            ['還不知道', s.unknowns],
            ['算成功', s.success],
          ] as const
        ).map(([k, v]) => (
          <React.Fragment key={k}>
            <Box component="dt" sx={{ color: 'text.secondary' }}>
              {k}
            </Box>
            <Box component="dd" sx={{ m: 0 }}>
              {v}
            </Box>
          </React.Fragment>
        ))}
      </Box>

      {focus.status === 'proposed' && !stale && (
        <Stack direction="row" useFlexGap spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <Button variant="contained" disableElevation disabled={busy} onClick={() => onChoose('main')} sx={{ flex: '1 1 160px', bgcolor: ACCENT, '&:hover': { bgcolor: '#174538' } }}>
            選這個
          </Button>
          {alternatives.map((a) => (
            <Button key={a.key} variant="outlined" color="inherit" disabled={busy} onClick={() => onChoose(a.key)}>
              改選 {a.key}
            </Button>
          ))}
        </Stack>
      )}

      <Box sx={{ display: 'grid', gap: 1 }}>
        {alternatives.map((a) => (
          <Box key={a.key} component="details" sx={{ border: 1, borderColor: 'divider', borderRadius: 2, px: 1.5, py: 1, '& summary': { listStyle: 'none', cursor: 'pointer' }, '& summary::-webkit-details-marker': { display: 'none' } }}>
            <Box component="summary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                替代 {a.key} · {a.title}
              </Typography>
              {a.metric && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  {a.metric}
                </Typography>
              )}
              <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary', ml: a.metric ? 0 : 'auto' }} />
            </Box>
            <Box sx={{ mt: 1, display: 'grid', gap: 0.5 }}>
              <Typography variant="body2">做什麼：{a.what}</Typography>
              <Typography variant="body2">為什麼不是第一：{a.why_not}</Typography>
              {a.cost && <Typography variant="body2">代價：{a.cost}</Typography>}
            </Box>
          </Box>
        ))}
      </Box>

      {focus.review && (
        <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 2, p: 1.5, display: 'grid', gap: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            週日檢討 · {focus.review.at}
          </Typography>
          <Typography variant="body2">有效：{focus.review.worked}</Typography>
          <Typography variant="body2">學到：{focus.review.learned}</Typography>
          <Typography variant="body2">下週：{focus.review.next}</Typography>
        </Box>
      )}
    </Paper>
  );
};

export default FocusCard;
